import { useEffect, useState, useMemo } from 'react';
import Navbar from '../components/Navbar.jsx';
import AwsCostPanel from '../components/AwsCostPanel.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../api/client.js';
import {
  Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const RANGES = [{ d: 7, label: '7d' }, { d: 30, label: '30d' }, { d: 90, label: '90d' }];

export default function Usage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/api/usage', { params: { days } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  const chart = useMemo(() => buildChart(data, days), [data, days]);

  return (
    <>
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium">Usage & cost</h1>
            <p className="text-sm text-muted mt-1">Across all your AI apps</p>
          </div>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button key={r.d} onClick={() => setDays(r.d)}
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  days === r.d ? 'bg-neutral-100 border-neutral-300' : 'border-border hover:bg-neutral-50'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className="text-muted">Loading…</p>}
        {data && <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Metric label="Total cost" value={`$${data.totals.cost.toFixed(2)}`} />
            <Metric label="API calls" value={data.totals.calls.toLocaleString()} />
            <Metric label="Input tokens" value={fmt(data.totals.inputTokens)} />
            <Metric label="Output tokens" value={fmt(data.totals.outputTokens)} />
          </div>

          <h2 className="text-base font-medium mb-3">Daily cost</h2>
          <div className="bg-white border border-border rounded-xl p-4 mb-8" style={{ height: 280 }}>
            <Bar data={chart.data} options={chart.options} />
          </div>

          {user?.role === 'ADMIN' && <AwsCostPanel />}

          <h2 className="text-base font-medium mb-3">Cost by app</h2>
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-muted">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">App</th>
                  <th className="text-right px-4 py-2.5 font-medium">Calls</th>
                  <th className="text-right px-4 py-2.5 font-medium">Input</th>
                  <th className="text-right px-4 py-2.5 font-medium">Output</th>
                  <th className="text-right px-4 py-2.5 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {[...data.byApp].sort((a, b) => b.cost - a.cost).map((row) => (
                  <tr key={row.app.id} className="border-t border-border">
                    <td className="px-4 py-2.5">{row.app.name}</td>
                    <td className="px-4 py-2.5 text-right">{row.calls.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-muted">{fmt(row.inputTokens)}</td>
                    <td className="px-4 py-2.5 text-right text-muted">{fmt(row.outputTokens)}</td>
                    <td className="px-4 py-2.5 text-right font-medium">${row.cost.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>}
      </main>
    </>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-neutral-50 rounded-lg p-4">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-2xl font-medium mt-1">{value}</div>
    </div>
  );
}

function fmt(n) {
  if (n > 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n > 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function buildChart(data, days) {
  if (!data) return { data: { labels: [], datasets: [] }, options: {} };
  const dayKeys = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    dayKeys.push(d.toISOString().slice(0, 10));
  }
  const apps = data.byApp.map((r) => r.app);
  const palette = ['#7F77DD', '#1D9E75', '#D85A30', '#378ADD', '#EF9F27', '#D4537E'];

  const datasets = apps.map((app, i) => ({
    label: app.name,
    backgroundColor: palette[i % palette.length],
    data: dayKeys.map((k) => {
      const row = data.daily.find((d) => d.appId === app.id && d.day.startsWith(k));
      return row ? Number(row.cost) : 0;
    }),
    borderRadius: 2
  }));

  return {
    data: {
      labels: dayKeys.map((k) => new Date(k).toLocaleDateString('en', { month: 'short', day: 'numeric' })),
      datasets
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } },
        y: { stacked: true, ticks: { font: { size: 10 }, callback: (v) => '$' + v } }
      }
    }
  };
}
